import { useExamCenter } from '@exam-center/exam-center-details';
import {
  type ExamEventData,
  formatRoomData,
  getApplicantLabelAndValue,
  getRegistrationStatusByCurrentStatus,
  sortRooms,
  transformRoomsToSelected,
  transformToLabelValueOptions,
  useExamEventDetails,
  useSessionManagement,
} from '@exam-center/exam-event-management';
import {
  ExamType,
  FilterOperator,
  GetAvailableRoomsByLocationIdAndTimeFrameDocument,
  GetExamEventByIdDocument,
  OrderType,
  RegistrationStatus,
  useGetAllProvincesQuery,
  useGetAvailableRoomsByLocationIdAndTimeFrameQuery,
  useGetExamCenterLocationsByProvinceIdQuery,
  useSearchExamCenterCoursesQuery,
  useUpdateExamEventMutation,
} from '@exam-center/graphql';
import { Loading } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { ExamEventTemporarilyClosedForm } from '../../components/exam-event-temporarily-closed-form';
import { useExamEventDetailsEditModal } from '../../providers';
import { getSnackbarMessageByNewStatus } from '../../utils';

import {
  getExamEventTemporarilyCloseFormSchema,
  type ExamEventTemporarilyClosedFormType,
} from './schema';

export default function TemporarilyCloseExamEventLogic() {
  const { examEvent } = useExamEventDetails();
  const { examCenter } = useExamCenter();

  const examEventDate = new Date(examEvent.examStartedAt);
  const month = examEvent.examStartedAt.getMonth();
  const year = examEventDate.getFullYear();
  const { setAction, close } = useExamEventDetailsEditModal();
  const { showPromiseSnackbar } = useSnackbar();

  const { data: examCenterCoursesData } = useSearchExamCenterCoursesQuery({
    variables: {
      searchExamCenterCoursesInput: {
        filter: {
          conditions: [
            {
              field: 'examCenterCode',
              operator: FilterOperator.Equal,
              value: examEvent.examCenterCode,
            },
          ],
        },
        paging: {
          currentPage: 0,
          pageSize: 200,
        },
        order: {
          type: OrderType.Asc,
          field: 'examId',
        },
      },
    },
    fetchPolicy: 'network-only',
  });

  const isAllExamCoursesSelected =
    examEvent.courses?.length === examCenterCoursesData?.ecSearchExamCenterCourses.total;

  const [updateExamEvent] = useUpdateExamEventMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetExamEventByIdDocument,
        variables: {
          getExamEventByIdInput: {
            id: examEvent.id,
          },
        },
      },
      GetAvailableRoomsByLocationIdAndTimeFrameDocument,
    ],
    onCompleted: () => {
      close();
    },
  });

  const getDefaultValues = useCallback(
    (examEvent: ExamEventData): ExamEventTemporarilyClosedFormType | undefined => {
      return {
        location: {
          province: transformToLabelValueOptions(examEvent.examLocation.province),
          locationName: transformToLabelValueOptions({
            id: examEvent.examLocation.id,
            nameTh: examEvent.examLocation.nameTh,
          }),
          room: examEvent.rooms ? transformRoomsToSelected(examEvent.rooms) : {},
          seat: examEvent.seat,
        },
        groupType: getApplicantLabelAndValue(examEvent.metadata?.applicantGroup?.type),
        groupCode: examEvent?.metadata?.applicantGroup?.code ?? '',
        status: examEvent.registrationStatus,
        eventStartDate: examEvent.registrationStartedAt ?? new Date(),
        eventEndDate: examEvent.registrationEndedAt,
      };
    },
    [],
  );

  const examEventTemporarilyCloseFormSchema = getExamEventTemporarilyCloseFormSchema(examEvent);

  const formContext = useForm<ExamEventTemporarilyClosedFormType>({
    defaultValues: async () => {
      return getDefaultValues(examEvent) as ExamEventTemporarilyClosedFormType;
    },
    resolver: zodResolver(examEventTemporarilyCloseFormSchema),
  });

  const { location, eventStartDate, eventEndDate } = formContext.watch();
  const isSelected = (value: Date | Record<string, string>) =>
    value !== undefined && value !== null;
  const isProvinceSelected = isSelected(location?.province);
  const isNameSelected = isSelected(location?.locationName);
  const locationRoom = Object.values(location?.room ?? {}).filter((value) => value);
  const isRoomSelected = locationRoom.length > 0;

  const setToNull = useCallback(
    (name: Parameters<typeof formContext.setValue>[0]) => {
      formContext.setValue(name, null, { shouldDirty: true });
    },
    [formContext],
  );

  const { data: useGetAllProvincesResult, loading: loadingProvincesOptions } =
    useGetAllProvincesQuery();

  const provincesOptions =
    useGetAllProvincesResult?.ecGetAllProvinces.provinces
      .map((province) => transformToLabelValueOptions(province))
      .sort((a, b) => a.label.localeCompare(b.label)) ?? [];

  const { data: useGetLocationsByProvinceIdResult, loading: loadingLocationOptions } =
    useGetExamCenterLocationsByProvinceIdQuery({
      variables: {
        getExamCenterLocationsByProvinceIdInput: {
          examCenterCode: examCenter.code,
          provinceId: location?.province?.value ?? '',
        },
      },
      skip: !isProvinceSelected,
    });

  const locationsOptions = useMemo(() => {
    return (
      useGetLocationsByProvinceIdResult?.ecGetExamCenterLocationsByProvinceId.examCenterLocations
        .map((location) =>
          transformToLabelValueOptions({
            id: location.location?.id ?? '',
            nameTh: location.location?.nameTh ?? '',
          }),
        )
        .sort((a, b) => a.label.localeCompare(b.label)) ?? []
    );
  }, [useGetLocationsByProvinceIdResult]);

  const {
    data: GetAvailableRoomsResult,
    loading: isGettingAvailableRooms,
    refetch: refetchGetAvailableRooms,
  } = useGetAvailableRoomsByLocationIdAndTimeFrameQuery({
    variables: {
      getAvailableRoomsByLocationIdAndTimeFrameInput: {
        locationId: examEvent.examLocation.id,
        startedAt: examEvent.examStartedAt,
        endedAt: examEvent.examEndedAt,
        examCenterCode: examCenter.code,
      },
    },
    skip: !examEvent.examLocation.id,
    fetchPolicy: 'cache-and-network',
  });

  const roomData = useMemo(() => {
    const availableRooms =
      GetAvailableRoomsResult?.ecGetAvailableRoomsByLocationIdAndTimeFrame.rooms.map(
        formatRoomData,
      ) ?? [];

    if (formContext.getValues('location.locationName.value') === examEvent.examLocation.id) {
      const examRooms = examEvent.rooms?.map(formatRoomData) ?? [];
      const uniqueRooms = Array.from(
        new Map(
          [...availableRooms, ...examRooms].map((room) => [JSON.stringify(room), room]),
        ).values(),
      );

      return uniqueRooms.sort(sortRooms);
    }

    return availableRooms.sort(sortRooms);
  }, [GetAvailableRoomsResult, examEvent, formContext]);

  const minimumExamDate = dayjs(new Date(year, month)).startOf('month').toDate();
  const maximumExamDate = dayjs(new Date(year, month)).endOf('month').toDate();

  const { sessions, totalDuration } = useSessionManagement(examEvent.sessions);

  const handleSubmit = async (data: ExamEventTemporarilyClosedFormType) => {
    if (data.status === RegistrationStatus.Canceled) {
      setAction({
        action: 'cancel',
        data: {
          onSubmit: (reason) => {
            handleShowPromiseSnackbar({
              ...data,
              reason,
            });
          },
        },
      });
    } else if (
      data.status === RegistrationStatus.TemporarilyClosed &&
      data.status !== examEvent.registrationStatus
    ) {
      setAction({
        action: 'temporarily-close',
        data: {
          onSubmit: (reason) => {
            handleShowPromiseSnackbar({
              ...data,
              reason,
            });
          },
        },
      });
    } else if (data.status === RegistrationStatus.OpenForRegistration) {
      setAction({
        action: 'open-for-registration',
        data: {
          onSubmit: () => {
            handleShowPromiseSnackbar(data);
          },
        },
      });
    } else {
      handleShowPromiseSnackbar(data);
    }
  };

  const handleUpdateExamEvent = useCallback(
    async (
      data: ExamEventTemporarilyClosedFormType & {
        reason?: string;
      },
    ) => {
      const updatedFields = formContext.formState.dirtyFields;
      const groupCode = (data.groupCode ?? '').length > 0 ? data.groupCode : null;
      const groupType = data.groupType?.value ?? null;

      const applicantGroup =
        updatedFields.groupCode || updatedFields.groupType
          ? {
              type: updatedFields.groupType ? groupType : undefined,
              code: updatedFields.groupCode ? groupCode : undefined,
            }
          : undefined;

      const formattedRooms = Object.entries(data.location.room)
        .filter(([, value]) => value)
        .map(([id]) => id);
      const result = await updateExamEvent({
        variables: {
          updateExamEventInput: {
            id: examEvent.id,
            data: {
              seat: updatedFields.location?.seat ? data.location.seat : undefined,
              roomIds: updatedFields.location?.room ? formattedRooms : undefined,
              registrationStartedAt: updatedFields.eventStartDate
                ? new Date(data.eventStartDate.toISOString())
                : undefined,
              registrationEndedAt: updatedFields.eventEndDate
                ? new Date(data.eventEndDate.toISOString())
                : undefined,
              examLocationId: updatedFields.location?.locationName
                ? data.location.locationName.value
                : undefined,
              metadata: {
                applicantGroup,
              },
            },
            toRegistrationStatus: updatedFields.status
              ? {
                  status: data.status,
                  reason: data.reason,
                }
              : undefined,
          },
        },
      });

      if (result.errors) {
        return Promise.reject(
          new Error(
            'Error updating exam event: ' +
              result.errors.map((error) => error.message ?? 'Unknown error').join(', '),
          ),
        );
      }

      return Promise.resolve();
    },
    [formContext.formState.dirtyFields, updateExamEvent, examEvent],
  );

  const handleShowPromiseSnackbar = useCallback(
    (
      data: ExamEventTemporarilyClosedFormType & {
        reason?: string;
      },
    ) => {
      const message = getSnackbarMessageByNewStatus({
        from: examEvent.registrationStatus,
        to: data.status,
      });

      return showPromiseSnackbar({
        message,
        promise: () => handleUpdateExamEvent(data),
      });
    },
    [examEvent, showPromiseSnackbar, handleUpdateExamEvent],
  );

  useEffect(() => {
    if (
      formContext.formState.dirtyFields.location?.room ||
      formContext.formState.dirtyFields.location?.seat
    ) {
      if (location?.room) {
        const selectedRoom = Object.entries(location.room)
          .filter(([, value]) => value)
          .map(([id]) => id);
        if (selectedRoom.length > 0) {
          const totalSeat = selectedRoom.reduce((acc, curr) => {
            const room = roomData.find((r) => r.id === curr);
            return acc + (room?.seat ?? 0);
          }, 0);
          formContext.setValue('location.seat', totalSeat, { shouldDirty: true });
        } else if (selectedRoom.length === 0 && isNameSelected) {
          setToNull('location.seat');
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationRoom.length, formContext, isNameSelected, location?.room]);

  useEffect(() => {
    const { unsubscribe } = formContext.watch((value, { name }) => {
      if (name === 'location.locationName') {
        refetchGetAvailableRooms({
          getAvailableRoomsByLocationIdAndTimeFrameInput: {
            locationId: value?.location?.locationName?.value ?? '',
            startedAt: examEvent.examStartedAt,
            endedAt: examEvent.examEndedAt,
            examCenterCode: examCenter.code,
          },
        });
        setToNull('location.room');
        setToNull('location.seat');
      }

      if (name === 'location.province') {
        setToNull('location.locationName');
        setToNull('location.room');
        setToNull('location.seat');
      }
    });

    return () => unsubscribe();
  }, [formContext, setToNull, refetchGetAvailableRooms, examEvent, examCenter.code]);

  useEffect(() => {
    if (formContext.formState.isSubmitSuccessful) {
      formContext.reset(getDefaultValues(examEvent));
    }
  }, [formContext, examEvent, getDefaultValues]);

  if (formContext.formState.isLoading) {
    return <Loading />;
  }

  return (
    <ExamEventTemporarilyClosedForm
      form={formContext}
      onSubmit={handleSubmit}
      slotProps={{
        examDate: {
          date: examEvent.examStartedAt,
          eventStartDate: eventStartDate,
          eventEndDate: eventEndDate,
          minDate: minimumExamDate,
          maxDate: maximumExamDate,
          isExamDateSelected: true,
          examEndedAt: examEvent.examEndedAt,
          examStartedAt: examEvent.examStartedAt,
        },
        examCourses: {
          isAllSelected: isAllExamCoursesSelected,
          courses: examEvent.courses
            .map((course) => course.examCourse?.name ?? null)
            .filter((name) => name !== null),
        },
        sessions: {
          data: examEvent.examType === ExamType.Computer ? sessions : [],
          totalDuration,
        },
        province: {
          isProvinceSelected,
          isProvincesOptionsLoading: loadingProvincesOptions,
          provincesOptions: provincesOptions,
          isLocationOptionsLoading: loadingLocationOptions,
          locationsOptions: locationsOptions,
          isGetAvailableRoomsLoading: isGettingAvailableRooms,
        },
        name: {
          isNameSelected,
        },
        room: {
          roomData,
          isRoomSelected,
          numberOfExaminees: examEvent.numberOfExaminees ?? 0,
          isShowSeatInfo: true,
        },
        examEventName: examEvent.name,
        examType: examEvent.examType,
        registrationType: examEvent.registrationType,
        examEventStatusOptions: getRegistrationStatusByCurrentStatus({
          currentStatus: examEvent.registrationStatus,
          isRegistrationStartedAtPassed: dayjs().isAfter(dayjs(examEvent.registrationStartedAt)),
        }),
        reason: examEvent.metadata.reason,
      }}
    />
  );
}
